import { Link, Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import useAuth from '../hooks/useAuth'
import heroIllustration from '../assets/images/hero-illustration.jpg'
import counselingSession from '../assets/images/counseling-session.jpg'
import LanguageSwitcher from '../components/LanguageSwitcher'

const ROLE_HOME = { admin: '/admin', facilitator: '/facilitator', client: '/client' }

export default function LandingPage() {
  const { t } = useTranslation('landing')
  const { user, role, loading } = useAuth()

  if (loading) return null
  if (user && role) return <Navigate to={ROLE_HOME[role] || '/login'} replace />

  return (
    <div className="min-h-screen bg-[#faf5ef]" style={{ fontFamily: "'Source Sans 3', system-ui, sans-serif" }}>
      {/* ===== HERO ===== */}
      <header className="relative overflow-hidden">
        {/* Warm gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary-800 via-[#234e3b] to-primary-800" />

        {/* Organic blob shape */}
        <div className="absolute -right-20 -top-20 w-[500px] h-[500px] opacity-10">
          <svg viewBox="0 0 500 500" fill="white">
            <path d="M250,50 C380,50 450,150 430,250 C410,350 350,430 250,450 C150,470 60,380 50,270 C40,160 120,50 250,50Z" />
          </svg>
        </div>
        <div className="absolute -left-32 bottom-0 w-[400px] h-[400px] opacity-[0.06]">
          <svg viewBox="0 0 400 400" fill="white">
            <circle cx="200" cy="200" r="200" />
          </svg>
        </div>

        {/* Nav */}
        <nav className="relative max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Interlinked circles icon */}
            <svg width="32" height="22" viewBox="0 0 32 22" fill="none">
              <circle cx="11" cy="11" r="9" stroke="#d4725c" strokeWidth="1.5" />
              <circle cx="21" cy="11" r="9" stroke="white" strokeWidth="1.5" opacity="0.6" />
            </svg>
            <span className="text-white font-semibold tracking-wide font-display">CSI</span>
          </div>
          <div className="flex items-center gap-5">
            <LanguageSwitcher />
            <Link
              to="/contact"
              className="text-sm font-medium text-white/80 hover:text-accent-400 transition-colors"
            >
              {t('nav.contactUs')}
            </Link>
            <Link
              to="/login"
              className="text-sm font-medium text-white/80 hover:text-accent-400 transition-colors"
            >
              {t('nav.signIn')}
            </Link>
          </div>
        </nav>

        {/* Hero content */}
        <div className="relative max-w-6xl mx-auto px-6 pt-12 pb-20">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left: Text */}
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/10 mb-8">
                <div className="w-2 h-2 rounded-full bg-accent-400" />
                <span className="text-white/70 text-sm">{t('hero.badge')}</span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-[3.5rem] text-white leading-[1.15] mb-6 font-display">
                {t('hero.titleStart')}
                <span className="text-accent-400">{t('hero.titleHighlight')}</span>{t('hero.titleEnd')}
              </h1>

              <p className="text-lg text-white/65 leading-relaxed mb-10 max-w-lg">
                {t('hero.description')}
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/facilitator/signup"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-accent-400 text-white font-semibold rounded-full transition-all hover:bg-accent-500 hover:shadow-lg hover:shadow-accent-400/25"
                >
                  {t('hero.registerButton')}
                </Link>
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-white/10 text-white font-semibold rounded-full border border-white/20 transition-all hover:bg-white/15"
                >
                  {t('hero.signInButton')}
                </Link>
              </div>
            </div>

            {/* Right: Hero illustration */}
            <div className="hidden md:block">
              <img
                src={heroIllustration}
                alt={t('hero.heroAlt')}
                className="rounded-2xl shadow-lg w-full h-auto"
              />
            </div>
          </div>
        </div>

        {/* Curved bottom edge */}
        <div className="relative h-16">
          <svg className="absolute bottom-0 w-full" viewBox="0 0 1440 64" fill="#faf5ef" preserveAspectRatio="none">
            <path d="M0,32 C360,64 720,0 1080,32 C1260,48 1380,56 1440,32 L1440,64 L0,64 Z" />
          </svg>
        </div>
      </header>

      {/* ===== ABOUT CSI ===== */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 text-accent-400 text-sm font-semibold tracking-wide uppercase mb-3">
              <div className="w-8 h-[1.5px] bg-accent-400" />
              {t('about.sectionLabel')}
              <div className="w-8 h-[1.5px] bg-accent-400" />
            </div>
            <h2 className="text-3xl md:text-4xl text-primary-800 font-display">
              {t('about.title')}
            </h2>
          </div>

          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-sm border border-primary-800/5">
            <div className="relative pl-6 border-l-[3px] border-accent-400/40">
              <p className="text-[#2a2a2a] text-[17px] leading-[1.9] mb-5">
                {t('about.paragraph1')}
              </p>
              <p className="text-[#2a2a2a] text-[17px] leading-[1.9] mb-5">
                {t('about.paragraph2')}
              </p>
              <p className="text-[#2a2a2a] text-[17px] leading-[1.9]">
                {t('about.paragraph3')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section className="py-20 px-6 bg-primary-800/[0.03]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 text-accent-400 text-sm font-semibold tracking-wide uppercase mb-3">
              <div className="w-8 h-[1.5px] bg-accent-400" />
              {t('howItWorks.sectionLabel')}
              <div className="w-8 h-[1.5px] bg-accent-400" />
            </div>
            <h2 className="text-3xl md:text-4xl text-primary-800 font-display">
              {t('howItWorks.title')}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 relative">
            {/* Connecting line */}
            <div className="hidden md:block absolute top-20 left-[17%] right-[17%] h-[2px] bg-gradient-to-r from-accent-400/30 via-primary-800/15 to-accent-400/30" />

            {[
              {
                num: 1,
                title: t('howItWorks.step1.title'),
                desc: t('howItWorks.step1.description'),
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" stroke="#1b3a2d" strokeWidth="1.5">
                    <circle cx="14" cy="9" r="5" />
                    <path d="M5 25c0-5 4-9 9-9s9 4 9 9" />
                    <path d="M19 7l2 2 3-3" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                )
              },
              {
                num: 2,
                title: t('howItWorks.step2.title'),
                desc: t('howItWorks.step2.description'),
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" stroke="#1b3a2d" strokeWidth="1.5">
                    <rect x="3" y="6" width="22" height="16" rx="2.5" />
                    <path d="M3 9l11 7 11-7" />
                  </svg>
                )
              },
              {
                num: 3,
                title: t('howItWorks.step3.title'),
                desc: t('howItWorks.step3.description'),
                icon: (
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" stroke="#1b3a2d" strokeWidth="1.5" strokeLinecap="round">
                    <path d="M4 24V8h5v16M11 24V13h5v11M18 24V5h5v19" />
                  </svg>
                )
              }
            ].map((step, i) => (
              <div key={i} className="relative text-center">
                {/* Circle number */}
                <div className="relative z-10 w-16 h-16 mx-auto mb-6 rounded-full bg-white border-2 border-accent-400/30 flex items-center justify-center shadow-sm">
                  <span className="text-accent-400 text-xl font-bold font-display">{step.num}</span>
                </div>

                <div className="bg-white rounded-2xl p-7 shadow-sm border border-primary-800/5 transition-all hover:shadow-md hover:border-accent-400/20">
                  <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-primary-800/[0.06] flex items-center justify-center">
                    {step.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-primary-800 mb-2 font-display">
                    {step.title}
                  </h3>
                  <p className="text-[#5a5a5a] text-[15px] leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== MID-PAGE IMAGE ===== */}
      <section className="py-8 px-6">
        <div className="max-w-5xl mx-auto">
          <img
            src={counselingSession}
            alt={t('midPageImageAlt')}
            className="rounded-2xl shadow-sm w-full h-auto"
          />
        </div>
      </section>

      {/* ===== FEATURES / BENEFITS ===== */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 text-accent-400 text-sm font-semibold tracking-wide uppercase mb-3">
              <div className="w-8 h-[1.5px] bg-accent-400" />
              {t('features.sectionLabel')}
              <div className="w-8 h-[1.5px] bg-accent-400" />
            </div>
            <h2 className="text-3xl md:text-4xl text-primary-800 font-display">
              {t('features.title')}
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                title: t('features.evidenceBased.title'),
                desc: t('features.evidenceBased.description'),
                icon: (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                    <path d="M12 2L2 7l10 5 10-5-10-5z" />
                    <path d="M2 17l10 5 10-5" />
                    <path d="M2 12l10 5 10-5" />
                  </svg>
                )
              },
              {
                title: t('features.actionableInsights.title'),
                desc: t('features.actionableInsights.description'),
                icon: (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                    <circle cx="11" cy="11" r="8" />
                    <path d="M21 21l-4.35-4.35" />
                    <path d="M11 8v6M8 11h6" />
                  </svg>
                )
              },
              {
                title: t('features.easyToUse.title'),
                desc: t('features.easyToUse.description'),
                icon: (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                    <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                    <path d="M22 4L12 14.01l-3-3" />
                  </svg>
                )
              },
              {
                title: t('features.professionalTool.title'),
                desc: t('features.professionalTool.description'),
                icon: (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                    <path d="M20 7h-9M14 17H5" />
                    <circle cx="17" cy="17" r="3" />
                    <circle cx="7" cy="7" r="3" />
                  </svg>
                )
              }
            ].map((feature, i) => (
              <div key={i} className="bg-white rounded-2xl p-7 shadow-sm border border-primary-800/5 text-center transition-all hover:shadow-md hover:-translate-y-1 hover:border-accent-400/20">
                <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-accent-400/10 text-accent-400 flex items-center justify-center">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold text-primary-800 mb-2 font-display">
                  {feature.title}
                </h3>
                <p className="text-[#5a5a5a] text-sm leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA BAND ===== */}
      <section className="relative py-20 px-6 bg-primary-800 overflow-hidden">
        {/* Organic shape decoration */}
        <div className="absolute -right-16 -top-16 w-80 h-80 opacity-[0.06]">
          <svg viewBox="0 0 200 200" fill="white">
            <circle cx="100" cy="100" r="100" />
          </svg>
        </div>
        <div className="absolute -left-24 -bottom-24 w-64 h-64 opacity-[0.04]">
          <svg viewBox="0 0 200 200" fill="white">
            <path d="M100,10 C170,10 190,80 180,130 C170,180 120,190 80,180 C40,170 10,130 20,80 C30,30 60,10 100,10Z" />
          </svg>
        </div>

        <div className="max-w-4xl mx-auto text-center relative">
          <div className="inline-flex items-center gap-2 text-accent-400 text-sm font-semibold tracking-wide uppercase mb-4">
            <div className="w-8 h-[1.5px] bg-accent-400" />
            {t('cta.sectionLabel')}
            <div className="w-8 h-[1.5px] bg-accent-400" />
          </div>
          <h2 className="text-2xl md:text-3xl text-white font-medium mb-4 font-display">
            {t('cta.title')}
          </h2>
          <p className="text-white/60 mb-10 text-lg max-w-xl mx-auto">
            {t('cta.description')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/facilitator/signup"
              className="px-8 py-3.5 bg-accent-400 text-white font-semibold rounded-full transition-all hover:bg-accent-500 hover:shadow-lg hover:shadow-accent-400/25"
            >
              {t('cta.registerButton')}
            </Link>
            <Link
              to="/login"
              className="px-8 py-3.5 border-2 border-white/20 text-white font-semibold rounded-full transition-all hover:bg-white/5 hover:border-white/40"
            >
              {t('cta.signInButton')}
            </Link>
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="py-10 px-6 border-t border-primary-800/8">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <svg width="24" height="16" viewBox="0 0 32 22" fill="none">
              <circle cx="11" cy="11" r="9" stroke="#d4725c" strokeWidth="1.5" />
              <circle cx="21" cy="11" r="9" stroke="#1b3a2d" strokeWidth="1.5" opacity="0.3" />
            </svg>
            <span className="text-sm text-primary-800/50 font-display">{t('footer.brandName')}</span>
          </div>
          <Link
            to="/contact"
            className="text-sm text-primary-800/50 hover:text-primary-800 transition-colors"
          >
            {t('footer.contactUs')}
          </Link>
          <p className="text-sm text-primary-800/30">
            {t('footer.copyright', { year: new Date().getFullYear() })}
            <span className="mx-1">·</span>
            {t('footer.developedBy')}{' '}
            <a href="mailto:patrickwangym@gmail.com" className="hover:text-primary-800 transition-colors">Patrick</a>
          </p>
        </div>
      </footer>
    </div>
  )
}
